import { useState } from "react";
import { 
  Settings, 
  Save, 
  Building2, 
  Phone, 
  Mail, 
  MapPin,
  Percent
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPanel = ({ 
  siteSettings, 
  setSiteSettings,
  financeSettings,
  setFinanceSettings,
  token
}) => {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("business");
  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/settings`, siteSettings, { headers });
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFinance = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/finance-settings`, financeSettings, { headers });
      toast.success("Finance settings saved");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save finance settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6" data-testid="settings-panel">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
        <p className="text-gray-600">Manage your business profile and configurations</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === "business" ? "default" : "outline"}
          onClick={() => setActiveTab("business")}
        >
          <Building2 className="w-4 h-4 mr-2" />
          Business Details
        </Button>
        <Button
          variant={activeTab === "finance" ? "default" : "outline"}
          onClick={() => setActiveTab("finance")}
        >
          <Percent className="w-4 h-4 mr-2" />
          Finance Options
        </Button>
      </div>

      {/* Business Details Tab */}
      {activeTab === "business" && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>Company Name</Label>
              <Input
                value={siteSettings.company_name || ""}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="DC Tiling & Bathrooms"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Contact Phone</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={siteSettings.phone || ""}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="07547 393 455"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Contact Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={siteSettings.email || ""}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="info@dctiling.co.uk"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Service Area</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={siteSettings.service_area || ""}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, service_area: e.target.value }))}
                  placeholder="Greater Manchester & Surrounding Areas"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Label>Business Address</Label>
              <Textarea
                value={siteSettings.address || ""}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter your business address..."
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="md:col-span-2">
              <Label>About / Company Description</Label>
              <Textarea
                value={siteSettings.about || ""}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, about: e.target.value }))}
                placeholder="Tell customers about your business..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Business Settings"}
            </Button>
          </div>
        </div>
      )}

      {/* Finance Options Tab */}
      {activeTab === "finance" && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-4">Finance Partner Settings</h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure finance options displayed on your website's finance page.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Finance Provider Name</Label>
                <Input
                  value={financeSettings.provider_name || ""}
                  onChange={(e) => setFinanceSettings(prev => ({ ...prev, provider_name: e.target.value }))}
                  placeholder="e.g., Kanda Finance"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Minimum Finance Amount (£)</Label>
                <Input
                  type="number"
                  value={financeSettings.min_amount || ""}
                  onChange={(e) => setFinanceSettings(prev => ({ ...prev, min_amount: parseInt(e.target.value) }))}
                  placeholder="1000"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Maximum Finance Amount (£)</Label>
                <Input
                  type="number"
                  value={financeSettings.max_amount || ""}
                  onChange={(e) => setFinanceSettings(prev => ({ ...prev, max_amount: parseInt(e.target.value) }))}
                  placeholder="25000"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Representative APR (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={financeSettings.apr || ""}
                  onChange={(e) => setFinanceSettings(prev => ({ ...prev, apr: parseFloat(e.target.value) }))}
                  placeholder="9.9"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Finance Terms & Conditions</Label>
              <Textarea
                value={financeSettings.terms || ""}
                onChange={(e) => setFinanceSettings(prev => ({ ...prev, terms: e.target.value }))}
                placeholder="Enter finance terms and conditions..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t">
            <Button onClick={handleSaveFinance} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Finance Settings"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
